import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaUsers,
  FaRegFileAlt,
  FaBell,
  FaCog,
  FaSignOutAlt,
  FaDownload,
  FaFilter,
  FaCalendarAlt,
  FaStore,
  FaChartBar,
  FaFileExport,
} from "react-icons/fa";
import { jsPDF } from "jspdf";
import Sidebar from "../components/SideBar";
import getTransactions from "../Functions/transactionsFunct";
import {
  ShimmerCard,
  ShimmerTable,
  ShimmerBox,
} from "../components/ShimmerComponents";
import useStore from "../store/useStore";

// Loading Spinner Component
const LoadingSpinner = ({ size = "w-6 h-6" }) => (
  <div
    className={`${size} animate-spin rounded-full border-2 border-blue-500 border-t-transparent`}
  ></div>
);

// Stats Card Component
const StatsCard = ({
  title,
  value,
  icon: Icon,
  color = "blue",
  loading = false,
}) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600 text-blue-600",
    green: "from-green-500 to-green-600 text-green-600",
    purple: "from-purple-500 to-purple-600 text-purple-600",
    orange: "from-orange-500 to-orange-600 text-orange-600",
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <div className="mt-1">
            {loading ? (
              <ShimmerBox width="w-16" height="h-8" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            )}
          </div>
        </div>
        <div
          className={`w-12 h-12 bg-gradient-to-br ${colorClasses[color]} rounded-full flex items-center justify-center text-white`}
        >
          <Icon className="text-lg" />
        </div>
      </div>
    </div>
  );
};

// Filter Section Component
const FilterSection = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  handleDownloadPDF,
  handleApplyFilter,
  showFilteredData,
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
    <div className="flex items-center space-x-2 mb-4">
      <FaFilter className="text-blue-500" />
      <h3 className="text-lg font-semibold text-gray-900">Filters & Actions</h3>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
      {/* Date Range */}
      <div className="lg:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <FaCalendarAlt className="inline mr-2" />
          Date Range
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          <span className="text-gray-500 text-sm">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Filter Button */}
      <div className="flex items-end">
        <button
          onClick={handleApplyFilter}
          className={`w-full px-4 py-2 rounded-lg transition-colors shadow-sm flex items-center justify-center space-x-2 ${
            showFilteredData
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-blue-600 hover:bg-blue-700 text-white"
          }`}
        >
          <FaFilter />
          <span>{showFilteredData ? "Show Week" : "Apply Filter"}</span>
        </button>
      </div>

      {/* Export Button */}
      <div className="flex items-end">
        <button
          onClick={handleDownloadPDF}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm flex items-center justify-center space-x-2"
        >
          <FaDownload />
          <span>Export PDF</span>
        </button>
      </div>
    </div>
  </div>
);

const Reports = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const cachedTransactions = useStore((state) => state.transactions);
  const transactionsLoaded = useStore((state) => state.transactionsLoaded);
  const setTransactionsGlobal = useStore((state) => state.setTransactions);

  const [transactions, setTransactions] = useState(cachedTransactions || []);
  const [transactionsLoading, setTransactionsLoading] = useState(!transactionsLoaded);
  const [showFilteredData, setShowFilteredData] = useState(false);

  // Set initial date range to last 7 days
  useEffect(() => {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    setEndDate(today.toISOString().split("T")[0]);
    setStartDate(weekAgo.toISOString().split("T")[0]);
  }, []);

  // Fetch transactions from Firebase
  useEffect(() => {
    const unsubscribe = getTransactions((data) => {
      // Sort transactions by date (newest first)
      const sortedTransactions = data.sort((a, b) => {
        const dateA =
          a.createdAt && typeof a.createdAt.toDate === "function"
            ? a.createdAt.toDate()
            : a.createdAt && a.createdAt.seconds
            ? new Date(
                a.createdAt.seconds * 1000 +
                  (a.createdAt.nanoseconds || 0) / 1000000
              )
            : new Date(a.createdAt);

        const dateB =
          b.createdAt && typeof b.createdAt.toDate === "function"
            ? b.createdAt.toDate()
            : b.createdAt && b.createdAt.seconds
            ? new Date(
                b.createdAt.seconds * 1000 +
                  (b.createdAt.nanoseconds || 0) / 1000000
              )
            : new Date(b.createdAt);

        return dateB - dateA; // Descending order (newest first)
      });

      setTransactions(sortedTransactions);
      setTransactionsGlobal(sortedTransactions); // Cache in store
      setTransactionsLoading(false);
    });

    // Cleanup subscription on component unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Function to process Firebase transactions into report format
  const processTransactionsToReportData = () => {
    const reportMap = new Map();
    const allServices = new Set(); // Track all unique services found

    transactions.forEach((transaction) => {
      let transactionDate;

      // Handle Firebase timestamp
      if (
        transaction.createdAt &&
        typeof transaction.createdAt.toDate === "function"
      ) {
        transactionDate = transaction.createdAt.toDate();
      } else if (transaction.createdAt && transaction.createdAt.seconds) {
        transactionDate = new Date(
          transaction.createdAt.seconds * 1000 +
            (transaction.createdAt.nanoseconds || 0) / 1000000
        );
      } else {
        transactionDate = new Date(transaction.createdAt);
      }

      const dateKey = transactionDate.toISOString().split("T")[0]; // YYYY-MM-DD format

      if (!reportMap.has(dateKey)) {
        reportMap.set(dateKey, {
          date: dateKey,
        });
      }

      const reportEntry = reportMap.get(dateKey);

      // Process each service in the transaction
      if (transaction.services && transaction.services.length > 0) {
        transaction.services.forEach((service) => {
          const serviceName = service.serviceName;
          const quantity = service.selectedQuantity || 1;

          // Add service to tracking set
          allServices.add(serviceName);

          // Initialize service count if not exists
          if (!reportEntry[serviceName]) {
            reportEntry[serviceName] = 0;
          }

          // Add quantity to service count
          reportEntry[serviceName] += quantity;
        });
      }
    });

    // Convert to array and sort by date
    const reportData = Array.from(reportMap.values()).sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    // Return both the report data and the list of all services
    return {
      reportData,
      allServices: Array.from(allServices).sort(),
    };
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleApplyFilter = () => {
    setShowFilteredData(!showFilteredData);
  };

  const handleDownloadPDF = () => {
    setIsLoading(true);
    // Simulate loading
    setTimeout(() => {
      const doc = new jsPDF("landscape");
      doc.setFontSize(16);
      doc.text("Report Data", 10, 10);
      doc.setFontSize(12);

      // Dynamic header based on available services
      let x = 10;
      doc.text("Date", x, 20);
      x += 30;

      allServices.forEach((service) => {
        doc.text(service, x, 20);
        x += 60; // Adjust spacing based on service name length
      });

      let y = 30;
      displayData.forEach((row) => {
        let currentX = 10;
        doc.text(row.date, currentX, y);
        currentX += 30;

        allServices.forEach((service) => {
          doc.text((row[service] || 0).toString(), currentX, y);
          currentX += 60;
        });
        y += 10;
      });

      doc.save("report_landscape.pdf");
      setIsLoading(false);
    }, 1000);
  };

  // Generate report data from Firebase transactions
  const { reportData, allServices } = processTransactionsToReportData();

  // Get initial week data (last 7 days)
  const getInitialWeekData = () => {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    return reportData.filter((row) => {
      const rowDate = new Date(row.date);
      return rowDate >= weekAgo && rowDate <= today;
    });
  };

  // Get filtered data based on date range
  const getFilteredData = () => {
    if (!startDate || !endDate) return [];
    return reportData.filter((row) => {
      const rowDate = new Date(row.date);
      return rowDate >= new Date(startDate) && rowDate <= new Date(endDate);
    });
  };

  // Choose which data to display
  const displayData = showFilteredData
    ? getFilteredData()
    : getInitialWeekData();

  // Calculate totals for stats (using display data)
  const totalServices = displayData.reduce((sum, row) => {
    return (
      sum +
      Object.keys(row)
        .filter((key) => key !== "date")
        .reduce((serviceSum, key) => serviceSum + (row[key] || 0), 0)
    );
  }, 0);

  // Calculate total for each service type
  const serviceTotals = {};
  allServices.forEach((service) => {
    serviceTotals[service] = displayData.reduce(
      (sum, row) => sum + (row[service] || 0),
      0
    );
  });

  // Get top 4 services for stats cards (based on currently displayed data only)
  const topServices = Object.entries(serviceTotals)
    .filter(([, total]) => total > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <main className="ml-80 p-8 lg:p-10 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Reports Dashboard
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">
              View and analyze your store performance data
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {transactionsLoading ? (
            // Show shimmer cards
            Array.from({ length: 4 }).map((_, index) => (
              <ShimmerCard key={index} />
            ))
          ) : topServices.length > 0 ? (
            // Show top 4 services
            topServices.map(([serviceName, total], index) => {
              const colors = ["blue", "green", "purple", "orange"];
              return (
                <StatsCard
                  key={serviceName}
                  title={serviceName}
                  value={total}
                  icon={FaChartBar}
                  color={colors[index % colors.length]}
                  loading={false}
                />
              );
            })
          ) : (
            // Show total services if no specific services
            <StatsCard
              title="Total Services"
              value={totalServices}
              icon={FaChartBar}
              color="blue"
              loading={false}
            />
          )}
        </div>

        {/* Filter Section */}
        <FilterSection
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          handleDownloadPDF={handleDownloadPDF}
          handleApplyFilter={handleApplyFilter}
          showFilteredData={showFilteredData}
        />

        {/* Report Table with Enhanced Scrolling */}
        {transactionsLoading ? (
          <ShimmerTable columns={allServices.length || 4} />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Report Data ({displayData.length} entries) -{" "}
                  {showFilteredData ? "Filtered" : "Last 7 Days"}
                </h3>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <FaFileExport />
                    <span>Export available</span>
                  </div>
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Scroll horizontally to view all services →
                  </div>
                </div>
              </div>
            </div>

            {/* Horizontal Scrollable Table Container */}
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <div className="min-w-max">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {/* Date column */}
                      <th className="sticky left-0 z-20 bg-gray-50 text-left p-3 text-gray-700 font-semibold border-b border-gray-200 min-w-[120px]">
                        Date
                      </th>

                      {/* Dynamic service columns */}
                      {allServices.map((service, index) => (
                        <th
                          key={service}
                          className="text-left p-3 text-gray-700 font-medium border-b border-gray-200 min-w-[120px] bg-blue-50"
                        >
                          {service}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.length ? (
                      displayData.map((row, index) => {
                        const formattedDate = new Date(
                          row.date
                        ).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        });
                        return (
                          <tr
                            key={index}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                          >
                            {/* Date column */}
                            <td className="sticky left-0 z-10 bg-white p-3 text-gray-900 font-medium border-r border-gray-200">
                              {formattedDate}
                            </td>

                            {/* Dynamic service columns */}
                            {allServices.map((service) => (
                              <td
                                key={service}
                                className="p-3 text-gray-900 bg-blue-25"
                              >
                                {row[service] || 0}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={allServices.length + 1}
                          className="p-12 text-center"
                        >
                          <FaRegFileAlt className="mx-auto text-4xl text-gray-300 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No data found
                          </h3>
                          <p className="text-gray-500">
                            Try adjusting your filters to see more results.
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Scroll indicator */}
            <div className="px-6 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
              Use horizontal scroll or trackpad to view all service columns
            </div>
          </div>
        )}

        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
              <LoadingSpinner />
              <span className="text-gray-700">Generating PDF...</span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Reports;
